import { Injectable, inject } from '@angular/core';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { map, Observable, switchMap, tap } from 'rxjs';
import { CollectionRequestBuilder } from '@web/common';
import { WarehouseReceiptCollectionItem } from '../models/warehouse-receipt-collection-item';
import { WarehouseReceiptCollectionFilter } from '../models/warehouse-receipt-collection-filter';
import { WarehouseReceiptApiService } from '../api/services/warehouse-receipt-api.service';
import {
  WarehouseReceiptFormData,
  WarehouseReceiptProductFormData,
} from '../models/warehouse-receipt-form-data';
import { WarehouseReceiptCreateRequest } from '../api/requests/warehouse-receipt-create.request';
import { WarehouseReceiptProductCreateRequest } from '../api/requests/warehouse-receipt-product-create.request';
import { blank, Guid, once, valueOrDefault } from '@web/utils';
import { WarehouseFileRequest } from '../api/requests/warehouse-file.request';
import { WarehouseReceiptUpdateRequest } from '../api/requests/warehouse-receipt-update.request';
import { WarehouseReceiptProductUpdateRequest } from '../api/requests/warehouse-receipt-product-update.request';
import { WarehouseReceiptDetails } from '../models/warehouse-receipt-details';
import { WarehouseReceiptProductDetails } from '../models/warehouse-receipt-product-details';
import { multimediaFromResponse } from '@web/shared/module/multimedia';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReceiptDataSourceService {
  private readonly warehouseReceiptApi = inject(WarehouseReceiptApiService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<WarehouseReceiptCollectionFilter>,
  ): Observable<TableDataPayload<WarehouseReceiptCollectionItem>> {
    return this.warehouseReceiptApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) => builder.equalFor('status').build())
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('goodsNames').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('goodsSkus').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('number').build(),
              )
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendDate((builder) => builder.rangeFor('receiptedAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<WarehouseReceiptCollectionItem>(
              response.items.map(
                (item) =>
                  new WarehouseReceiptCollectionItem({
                    id: item.id,
                    number: item.number,
                    receiptedAt: item.receiptedAt,
                    createdAt: item.createdAt,
                    status: item.status,
                    productSkus: item.productSkus,
                    productNames: item.productNames,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeViewed: item.contextOperations.canBeViewed,
                    canBeApproved: item.contextOperations.canBeApproved,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchDetailsFormById(id: Guid): Observable<WarehouseReceiptFormData> {
    return this.warehouseReceiptApi.fetchDetailsById(id).pipe(
      switchMap((response) =>
        this.warehouseReceiptApi
          .fetchReceiptProductCollection(
            id,
            this.collectionRequestBuilder.disablePagination().build(),
          )
          .pipe(
            map(({ items }) => new WarehouseReceiptFormData({
              products: items.map((product) => new WarehouseReceiptProductFormData({
                batchNumber: product.batch,
                brand: product.brand,
                durabilityInformation: product.durabilityInformation,
                id: product.id,
                name: product.name,
                ownerId: product.owner.id,
                promotion: product.promotion,
                quantity: product.quantity,
                sku: product.sku,
                type: product.type,
                unit: product.quantityUnit,
              })),
              comment: response.comment,
              receiptedAt: response.receiptedAt,
              fileIds: response.files.map((file) => file.id),
            })),
          ),
      ),
    );
  }

  public fetchDetailsById(id: Guid): Observable<WarehouseReceiptDetails> {
    return this.warehouseReceiptApi.fetchDetailsById(id).pipe(
      switchMap((response) =>
        this.warehouseReceiptApi
          .fetchReceiptProductCollection(
            id,
            this.collectionRequestBuilder.disablePagination().build(),
          )
          .pipe(
            map(
              ({ items }) =>
                new WarehouseReceiptDetails({
                  id: response.id,
                  status: response.status,
                  receiptedAt: response.receiptedAt,
                  createdAt: response.createdAt,
                  approvedAt: response.approvedAt,
                  number: response.number,
                  comment: response.comment,
                  files: response.files.map((file) =>
                    multimediaFromResponse(file),
                  ),
                  products: items.map(
                    (product) =>
                      new WarehouseReceiptProductDetails({
                        batchNumber: product.batch,
                        brand: product.brand,
                        durabilityInformation: product.durabilityInformation,
                        id: product.id,
                        name: product.name,
                        ownerId: product.owner.id,
                        promotion: product.promotion,
                        quantity: product.quantity,
                        sku: product.sku,
                        type: product.type,
                        unit: product.quantityUnit,
                      }),
                  ),
                }),
            ),
          ),
      ),
    );
  }

  public approveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz zatwierdzić przyjęcie zewnętrzne?')
          .accept(() => {
            this.warehouseReceiptApi
              .approveById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Przyjęcie zewnętrzne zostało zatwierdzone pomyślnie.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public createByFormData(data: WarehouseReceiptFormData): Observable<void> {
    return this.warehouseReceiptApi
      .create(
        new WarehouseReceiptCreateRequest(
          data.receiptedAt,
          data.products.map(
            (product) =>
              new WarehouseReceiptProductCreateRequest(
                product.ownerId!,
                product.sku!,
                product.type!,
                product.batchNumber!,
                product.name!,
                product.brand!,
                product.promotion!,
                product.durabilityInformation!,
                product.quantity!,
                product.unit!,
              ),
          ),
          valueOrDefault(data.fileIds, []).map(
            (id) => new WarehouseFileRequest(id),
          ),
          data.comment,
        ),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess('Przyjęcie zewnętrzne zostało dodane pomyślnie.')
              .build(),
          );
        }),
        blank(),
      );
  }

  public updateByFormData(
    id: Guid,
    data: WarehouseReceiptFormData,
  ): Observable<void> {
    return this.warehouseReceiptApi
      .update(
        id,
        new WarehouseReceiptUpdateRequest(
          data.receiptedAt,
          data.products.map(
            (product) =>
              new WarehouseReceiptProductUpdateRequest(
                product.id!,
                product.ownerId!,
                product.sku!,
                product.type!,
                product.batchNumber!,
                product.name!,
                product.brand!,
                product.promotion!,
                product.durabilityInformation!,
                product.quantity!,
                product.unit!,
              ),
          ),
          valueOrDefault(data.fileIds, []).map(
            (id) => new WarehouseFileRequest(id),
          ),
          data.comment,
        ),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess(
                'Przyjęcie zewnętrzne zostało zaktualizowane pomyślnie.',
              )
              .build(),
          );
        }),
        blank(),
      );
  }
}
